import axios from 'axios';
import i18next from 'i18next';
import { Category } from 'src/models';
import { getRootStoreSingleton } from 'src/models/root-store/root-store-singleton';

import { DEFAULT_AXIOS_CONFIG } from './axiosConfig';

// 토큰 갱신 중복 방지
let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

function onRefreshed(token: string | null) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
}

// Axios 인스턴스 생성
const axiosInstance = axios.create(DEFAULT_AXIOS_CONFIG);

// 요청 인터셉터 설정
axiosInstance.interceptors.request.use(
  (config) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(true);

    const token = getRootStoreSingleton().authStore.getAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // i18next.language를 언어 소스로 직접 사용
    config.headers['Accept-Language'] = i18next.language || 'ko';
    return config;
  },
  (error) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(false);
    return Promise.reject(error);
  },
);

// 응답 인터셉터 설정
axiosInstance.interceptors.response.use(
  async (response) => {
    const data = response.data;
    const config = response.config;

    const authStore = getRootStoreSingleton().authStore;
    const snackbarStore = getRootStoreSingleton().snackbarStore;
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(false);

    if (data.resultCode === 'F') {
      // 처방코드 자동 조회 실패는 회원가입 화면에서 모달로 직접 처리하므로 스낵바 스킵
      if (config.url?.endsWith('/prescriptionCode/lookup')) {
        return response;
      }

      if (
        (data.errorCode === '401' || data.errorCode === '4001') &&
        !config.url?.includes('/login/refresh')
      ) {
        // 이미 다른 요청이 refresh 중이면 대기열에 등록
        if (isRefreshing) {
          return new Promise((resolve) => {
            addRefreshSubscriber((token: string | null) => {
              if (!token) {
                // refresh 실패 → 원래 응답 그대로 반환 (snackbar 중복 방지)
                resolve(response);
                return;
              }
              config.headers['Authorization'] = `Bearer ${token}`;
              config.headers['Accept-Language'] = i18next.language || 'ko';
              resolve(axiosInstance(config));
            });
          });
        }

        // 첫 번째 401 요청이 refresh 담당
        isRefreshing = true;

        const refreshToken = authStore.getRefreshToken();
        if (refreshToken) {
          const result = await authStore.loginRefresh(refreshToken);
          if (result?.resultCode === 'S') {
            const oauthToken = result.data;
            if (oauthToken) {
              authStore.setAccessToken(oauthToken?.access_token);
              authStore.setRefreshToken(oauthToken?.refresh_token);
            }

            const newToken = authStore.getAccessToken() || '';
            isRefreshing = false;
            onRefreshed(newToken);

            config.headers['Authorization'] = `Bearer ${newToken}`;
            config.headers['Accept-Language'] = i18next.language || 'ko';
            return axiosInstance(config);
          } else {
            isRefreshing = false;
            onRefreshed(null);
            snackbarStore.setProps({
              open: true,
              category: Category.Error,
              title: result?.errorMessage,
            });
            authStore.initAuth();
          }
        } else {
          isRefreshing = false;
          onRefreshed(null);
          authStore.initAuth();
        }
      } else if (data.errorCode !== '409') {
        let errorMessage = '' as string | undefined;
        if (data?.errorMessage) {
          errorMessage = data?.errorMessage;
        } else if (data?.err_msg) {
          errorMessage = data?.err_msg;
        }

        // 기술적 에러 메시지(JDBC, SQL 등)가 사용자에게 노출되지 않도록 필터링
        const technicalErrorPattern =
          /JDBC|SQL|Exception|Unknown column|conn=|TB_|java\.|org\.|springframework/i;
        if (errorMessage && technicalErrorPattern.test(errorMessage)) {
          console.error('[API 기술 에러]', errorMessage);
          errorMessage = i18next.t('common.commonError');
        }

        snackbarStore.setProps({
          open: true,
          category: Category.Error,
          title: errorMessage ? errorMessage : i18next.t('common.commonError'),
        });
      }
    }

    return response;
  },
  (error) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(false);

    const snackbarStore = getRootStoreSingleton().snackbarStore;
    if (error.code === 'ERR_NETWORK') {
      snackbarStore.setProps({
        open: true,
        category: Category.Error,
        title: i18next.t('common.serverError'),
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
